import React from 'react';
import clsx from 'clsx';
import { Formik, useFormikContext } from 'formik';

import { makeStyles } from 'tss-react/mui';
import { useIsMobile, mobileBreakpoint, useL10n } from '@uplift/hooks';
import { CancelButton, FormModal, ModalActions, ModalContent, MoneyCents, SubmitButton, useAlert } from '@uplift/ui-v2';

import { BillingAddressForm, BillingFormType, CardDetailsForm, useBillingForm } from '../billing-form';
import { useSettingsStyles } from '../../../../settings/styles';
import { isApiError } from '@uplift/api';

export type EditPaymentMethodModalProps = {
  onCancel: () => void;
  onSubmit: (resourceIds: null | string[]) => Promise<void>;
  balance: number;
};


export const EditPaymentMethodModalRaw: React.FC<EditPaymentMethodModalProps> = ({ onCancel, balance }) => {
  const l10n = useL10n();
  const { classes: settingsClasses } = useSettingsStyles();
  const { classes } = useStyles();
  const isMobile = useIsMobile(mobileBreakpoint);
  const formikContext = useFormikContext<BillingFormType>();

  const buttonTitle = React.useMemo(() => {
    const balanceToPay = formikContext.values.payments?.reduce((acc, payment) => acc + payment.amountCents, 0) ?? balance;

    if (balanceToPay) {
      return <span>{l10n.accountSettings.billing.updateAndPay} <MoneyCents value={balanceToPay} /></span>;
    }

    return l10n.general.save;
  }, [ balance, formikContext.values.payments, l10n ]);

  return (
    <FormModal
      fullScreen={isMobile}
      open
      onClose={onCancel}
      title={l10n.accountSettings.billing.editPaymentMethod}
      fullWidth
      maxWidth="md"
      classes={{ paperWidthMd: settingsClasses.paperWidthMd, root: !isMobile && classes.modalPadding }}
    >
      <ModalContent
        dividers
        noPadding
        className={clsx(classes.paymentMethod, {
          content: settingsClasses.mobileModalContent,
          root: settingsClasses.modalContent,
        })}
      >
        <CardDetailsForm isMobile={isMobile} />
        <BillingAddressForm />
      </ModalContent>
      <ModalActions className={isMobile ? settingsClasses.mobileModalActions : null}>
        <CancelButton variant="outlined" onClick={onCancel} fullWidth>
          {l10n.general.cancel}
        </CancelButton>
        <SubmitButton variant="contained" color="secondary" fullWidth>
          {buttonTitle}
        </SubmitButton>
      </ModalActions>
    </FormModal>
  );
};

export const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({ onCancel, onSubmit, balance }) => {
  const alert = useAlert();
  const { billingFormik } = useBillingForm();
  const { onSubmit: billingOnSubmit, ...billingProps } = billingFormik();

  return (
    <Formik
      onSubmit={async (values, actions) => {
        const billingResult = await billingOnSubmit(values, actions);

        if (billingResult && billingResult.success) {
          try {
            await onSubmit(values.payments?.map(payment => payment.resourceId) ?? null);
          } catch (err) {
            if (isApiError(err)) {
              alert.error(`Unable to pay balance. ${err.response.data.error}`);
            } else {
              alert.error(`Unable to pay balance. ${err.message}`);
            }
          }
        }
      }}
      {...billingProps}
    >
      <EditPaymentMethodModalRaw
        onCancel={onCancel}
        onSubmit={onSubmit}
        balance={balance}
      />
    </Formik>
  );
};

const useStyles = makeStyles()((theme) => ({
  modalPadding: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  paymentMethod: {
    backgroundColor: theme.palette.grey[50],
    [theme.breakpoints.up('sm')]: {
      backgroundColor: theme.palette.common.white,
    },
    '& label.Mui-focused:not(.Mui-error)': {
      color: theme.palette.secondary.main,
    },
    '& div.Mui-focused:not(.Mui-error) fieldset': {
      borderColor: `${theme.palette.secondary.main} !important`,
    },
  },
  alert: {
    marginBottom: theme.spacing(2),
    width: '380px'
  }
}));
